// Owner-supplied Privacy Policy content (July 2026), rendered ABOVE the pre-existing
// policy on the same /privacy page. Existing content is preserved untouched below.
const SECTIONS = [
  {
    n: 1,
    title: "Our Privacy Commitment",
    blocks: [
      { type: "p", text: "Nova was built with privacy first." },
      { type: "p", text: "Our goal is simple:" },
      { type: "ul", items: [
        "Your financial information belongs to you.",
        "We do not sell your information.",
        "We do not use your financial information for advertising.",
        "We only process information required to provide Nova’s features.",
        "You remain in control of your data.",
      ] },
    ],
  },
  {
    n: 2,
    title: "Information You Provide",
    blocks: [
      { type: "p", text: "You may choose to provide information including:" },
      { type: "ul", items: [
        "Name", "Email address", "Debts", "Savings goals", "Income", "Expenses",
        "Repayment plans", "Budgets", "Financial goals", "Payment history",
        "Recurring expenses", "Financial notes",
      ] },
      { type: "p", text: "This information is entirely optional and is only used to provide Nova’s functionality." },
    ],
  },
  {
    n: 3,
    title: "Bank Statement Analysis",
    blocks: [
      { type: "p", text: "Nova allows you to upload PDF bank statements to generate AI-powered financial insights." },
      { type: "p", text: "When you upload a bank statement, Nova may analyse information such as:" },
      { type: "ul", items: [
        "Income", "Spending", "Transactions", "Merchant names", "Categories",
        "Recurring subscriptions", "Bills", "Payment dates", "Spending trends",
      ] },
      { type: "p", text: "This analysis helps Nova automatically identify recurring expenses, build budgets, detect subscriptions, and provide financial coaching." },
      { type: "p", text: "Your uploaded bank statements are processed only to generate the analysis you request. Nova does not permanently store your uploaded bank statements on its servers." },
    ],
  },
  {
    n: 4,
    title: "AI Processing",
    blocks: [
      { type: "p", text: "Nova uses artificial intelligence to help you understand your finances." },
      { type: "p", text: "AI may be used to:" },
      { type: "ul", items: [
        "Analyse spending", "Detect recurring expenses", "Categorise transactions",
        "Suggest repayment strategies", "Forecast debt completion dates",
        "Build repayment schedules", "Generate financial insights",
        "Answer financial questions through Nova Coach",
      ] },
      { type: "p", text: "AI responses are generated using the information you provide and may not always be accurate or complete." },
      { type: "p", text: "Nova provides organisational and educational tools only and does not provide regulated financial advice." },
    ],
  },
  {
    n: 5,
    title: "Where Your Data Is Stored",
    blocks: [
      { type: "p", text: "Nova has been designed to minimise the amount of information stored centrally." },
      { type: "p", text: "Your financial information remains under your control." },
      { type: "p", text: "Depending on the features you use, your information may be stored:" },
      { type: "ul", items: [
        "On your own device.",
        "Within your own authenticated Nova account if Cloud Backup is enabled.",
      ] },
      { type: "p", text: "Nova does not permanently store uploaded bank statements after they have been analysed." },
    ],
  },
  {
    n: 6,
    title: "Cloud Backup",
    blocks: [
      { type: "p", text: "If you choose to enable Cloud Backup:" },
      { type: "ul", items: [
        "Your Nova data may be securely backed up.",
        "Backups are associated only with your authenticated Nova account.",
        "You may restore your backups at any time.",
        "You may disable backups whenever you wish.",
        "You may permanently delete your backups.",
      ] },
      { type: "p", text: "Cloud Backup exists solely to help you recover your own information across devices." },
    ],
  },
  {
    n: 7,
    title: "Authentication",
    blocks: [
      { type: "p", text: "Nova supports:" },
      { type: "ul", items: ["Email & Password", "Sign in with Apple", "Sign in with Google"] },
      { type: "p", text: "Authentication providers operate under their own privacy policies." },
      { type: "p", text: "Nova never receives or stores your Apple or Google passwords." },
    ],
  },
  {
    n: 8,
    title: "Notifications",
    blocks: [
      { type: "p", text: "If enabled, Nova may send reminders such as:" },
      { type: "ul", items: [
        "Bill reminders", "Debt payment reminders", "Savings reminders",
        "Goal milestones", "Subscription reminders", "Security notifications",
      ] },
      { type: "p", text: "Notifications can be disabled at any time within Settings." },
    ],
  },
  {
    n: 9,
    title: "How We Use Your Information",
    blocks: [
      { type: "p", text: "Information is processed only to:" },
      { type: "ul", items: [
        "Operate Nova.", "Provide AI-powered financial insights.",
        "Analyse uploaded bank statements.", "Track debts and goals.",
        "Generate reports.", "Create repayment schedules.",
        "Detect recurring expenses.", "Synchronise backups.",
        "Improve app stability.", "Protect user accounts.",
        "Prevent fraud and abuse.",
      ] },
      { type: "p", text: "We do not use your financial information for advertising." },
    ],
  },
  {
    n: 10,
    title: "Information We Do Not Sell",
    blocks: [
      { type: "p", text: "Nova does not sell:" },
      { type: "ul", items: [
        "Personal information.", "Financial information.", "Spending history.",
        "Income information.", "Debt information.", "Bank statements.",
        "AI conversations.",
      ] },
      { type: "p", text: "We do not share your financial information with advertisers or data brokers." },
    ],
  },
  {
    n: 11,
    title: "Security",
    blocks: [
      { type: "p", text: "Nova uses industry-standard security measures including:" },
      { type: "ul", items: [
        "Encrypted network communication (HTTPS/TLS)", "Secure authentication",
        "Password hashing", "Secure session management", "Access controls",
        "Security monitoring",
      ] },
      { type: "p", text: "While we take reasonable steps to protect your information, no system can guarantee absolute security." },
    ],
  },
  {
    n: 12,
    title: "Your Rights",
    blocks: [
      { type: "p", text: "Depending on your location, you may have the right to:" },
      { type: "ul", items: [
        "Access your information.", "Correct inaccurate information.",
        "Delete your account.", "Delete your backups.", "Export your data.",
        "Withdraw consent where applicable.",
      ] },
      { type: "p", text: "You may also delete your Nova account directly from within the application." },
    ],
  },
  {
    n: 13,
    title: "Children’s Privacy",
    blocks: [
      { type: "p", text: "Nova is not intended for individuals under the age of 18 or the minimum legal age required in your jurisdiction." },
      { type: "p", text: "We do not knowingly collect information from children." },
    ],
  },
  {
    n: 14,
    title: "Third-Party Services",
    blocks: [
      { type: "p", text: "Nova integrates with trusted third-party providers where necessary, including:" },
      { type: "ul", items: [
        "Apple Sign-In", "Google Sign-In", "AI processing providers",
        "Cloud infrastructure providers", "Notification services",
      ] },
      { type: "p", text: "These providers process information only as necessary to deliver the requested functionality and are subject to their own privacy policies." },
    ],
  },
  {
    n: 15,
    title: "Changes to This Privacy Policy",
    blocks: [
      { type: "p", text: "We may update this Privacy Policy from time to time." },
      { type: "p", text: "If significant changes are made, we will notify users within the app or by other appropriate means." },
      { type: "p", text: "The latest version will always be available within Nova and on our official website." },
    ],
  },
  {
    n: 16,
    title: "Contact Us",
    blocks: [
      { type: "p", text: "If you have any questions about this Privacy Policy or how your information is handled, please contact us through the Nova Support Centre within the app or via the official Nova website." },
    ],
  },
  {
    n: 17,
    title: "Your Consent",
    blocks: [
      { type: "p", text: "By creating a Nova account or continuing to use the application, you confirm that you have read, understood, and agree to this Privacy Policy." },
      { type: "p", text: "Nova is built on one simple principle: your financial information belongs to you. Our mission is to help you understand your money while keeping your privacy at the centre of everything we do." },
    ],
  },
];

const Block = ({ block }) =>
  block.type === "ul" ? (
    <ul className="mt-2 grid gap-2 sm:grid-cols-2">
      {block.items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-nova-emerald" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  ) : (
    <p>{block.text}</p>
  );

export const NewPrivacyPolicy = () => (
  <div data-testid="privacy-new-content" className="space-y-10">
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-white/40">
        Effective July 2026 · Application: Nova · Last updated July 2026
      </p>
      <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-white/70">
        <p>Thank you for using Nova.</p>
        <p>
          Your privacy is fundamental to everything we build. Nova has been designed around the principle
          that your financial information belongs to you. We do not sell your data, build advertising
          profiles, or monetise your personal financial information.
        </p>
        <p>This Privacy Policy explains what information Nova processes, why it is processed, and how your information is protected.</p>
        <p>By creating an account or using Nova, you agree to this Privacy Policy.</p>
      </div>
    </div>

    {SECTIONS.map((s) => (
      <section key={s.n} className="scroll-mt-28">
        <h3 className="font-heading text-lg font-bold text-white sm:text-xl">{`${s.n}. ${s.title}`}</h3>
        <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-white/65">
          {s.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </div>
      </section>
    ))}
  </div>
);

export default NewPrivacyPolicy;
